import type { DirectoryElementsWithPermissions, DirectoryPublic } from '@repo/types/directory';
import type { ReactElement } from 'react';

import { useEditDirectories } from '@repo/api/directory/useEditDirectories';
import { useGetDirectory } from '@repo/api/directory/useGetDirectory';
import { useEditFiles } from '@repo/api/file/useEditFiles';
import { cn } from '@repo/lib/cn';
import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { CheckCircle2, ChevronRight, FolderInput, FolderKey, FolderLock, Folders, HardDrive } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import FSPath from '@/components/features/files/FsPath';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { List, listElementVariants, ListItem, ListItemTitle } from '@/components/ui/list/List';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Skeleton } from '@/components/ui/skeleton/Skeleton';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { useFilesStorage } from '@/store/files-store';

interface FsEntryElement {
  dir: DirectoryPublic;
  isSelected: boolean;
  isWritable: boolean;
}

/**
 * Props for the MoveFSEntry component.
 * @property {DirectoryElementsWithPermissions} elements - Elements to move.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(open: boolean) => void} onOpenChange - Callback for dialog open state.
 * @property {() => void} [callback] - Optional callback after move.
 * @property {string} [directoryId] - Directory ID context for move.
 */
interface MoveFSEntryProps {
  elements: DirectoryElementsWithPermissions;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback?: () => void;
  directoryId?: string;
}

export default function MoveFSEntry(moveFSEntryProps: MoveFSEntryProps) {
  const { elements, open, onOpenChange, callback, directoryId } = moveFSEntryProps;
  const { id } = useParams();
  const { model } = useFilesStorage();

  const { directoryId: defaultDirectoryId } = useCredentials();
  const pageDirId = useMemo(() => {
    return id ? id : defaultDirectoryId;
  }, [id, defaultDirectoryId]);

  const [openAlertDialog, setOpenAlertDialog] = useState<boolean>(false);
  const [currentDirectoryId, setCurrentDirectoryId] = useState<string>(pageDirId);
  const [selectedDir, setSelectedDir] = useState<DirectoryPublic | null>(null);

  const { data: directory, isLoading, isSuccess, isError } = useGetDirectory(currentDirectoryId, model === 'MY_DRIVE');

  const { executeMutations: filesMutations, isPending: filesMutationIsPending } = useEditFiles(directoryId);
  const { executeMutations: directoriesMutations, isPending: directoriesMutationIsPending } = useEditDirectories(directoryId);

  const isPending = useMemo(() => filesMutationIsPending || directoriesMutationIsPending, [directoriesMutationIsPending, filesMutationIsPending]);

  const directories = useMemo(() => {
    if (!isSuccess) {
      return [];
    }
    return directory.childDirs.map((dir) => {
      if (elements.directories.includes(dir)) {
        return {
          dir: dir,
          isSelected: true,
          isWritable: dir.permissions.write,
        } as FsEntryElement;
      }
      return {
        dir: dir,
        isSelected: false,
        isWritable: dir.permissions.write,
      } as FsEntryElement;
    });
  }, [directory, elements.directories, isSuccess]);

  const isNoDeeper = directories.length === 0;

  const onChangeSelect = (dir: DirectoryPublic) => {
    if (dir.id === selectedDir?.id) {
      setSelectedDir(null);
    } else {
      setSelectedDir(dir);
    }
  };

  const onRouteChange = (id?: string) => {
    setCurrentDirectoryId(id || pageDirId);
    setSelectedDir(null);
  };

  const onAlertDialogOpen = () => {
    setOpenAlertDialog(true);
  };

  const onSubmit = async () => {
    if (selectedDir === null) {
      return;
    }
    const { files, directories } = { ...elements };

    await filesMutations(files.map((file) => ({ id: file.id, directoryId: selectedDir.id }))).catch((error) => {
      toasterMessageHandler(fsErrorMessages, error);
    });
    await directoriesMutations(
      directories.map((directory) => ({
        id: directory.id,
        parentDirId: selectedDir.id,
      }))
    ).catch((error) => {
      toasterMessageHandler(fsErrorMessages, error);
    });

    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
    setOpenAlertDialog(false);
    setSelectedDir(null);
    setCurrentDirectoryId(pageDirId);
    callback?.();
  };

  useEffect(() => {
    if (directory) {
      setSelectedDir(directory);
    }
  }, [directory]);

  return (
    <>
      <AlertDialog open={openAlertDialog} onOpenChange={setOpenAlertDialog}>
        <AlertDialogContent className={'sm:max-w-96'}>
          <AlertDialogBody>
            <AlertDialogHeader>
              <FeaturedIcon>
                <Folders />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <AlertDialogTitle>Przenieś element/y ?</AlertDialogTitle>
                <p className={'text-text-tertiary text-xs'}>Ten element będzie widoczny dla wszystkich użytkowników, którzy mogą wyświetlać folder „{selectedDir?.name}”</p>
              </div>
            </AlertDialogHeader>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
              Zamknij
            </AlertDialogCancel>
            <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={'sm:max-w-148'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
          <DialogDescription className={'sr-only'}>Formularz do przenoszenia plików</DialogDescription>
          <DialogBody>
            <DialogHeader className={'border-border-primary border-b'}>
              <DialogTitle>Przenieś</DialogTitle>
            </DialogHeader>
            <div className={'flex grow flex-col gap-6 overflow-auto p-4'}>
              <div className={'flex flex-col gap-4'}>
                <Button size={'sm'} onClick={() => onRouteChange(pageDirId)} variant={'outline'} className={'w-fit'}>
                  <HardDrive />
                  Aktualna lokacja
                </Button>
                <FSPath directory={directory} isLoading={isLoading} isError={isError} onRouteChange={onRouteChange} />
              </div>
              <List>
                {isNoDeeper && (
                  <li className="p-3">
                    <p className={'text-text-tertiary my-3 text-center text-sm'}>Brak folderów</p>
                  </li>
                )}
                {directories.map((element) => (
                  <MoveFsEntryElement
                    key={element.dir.id}
                    isCurrentSelected={selectedDir?.id === element.dir.id}
                    element={element}
                    onOpenSummarize={onAlertDialogOpen}
                    onRouteChange={onRouteChange}
                    selectDirectory={onChangeSelect}
                  />
                ))}
                {isLoading && (
                  <>
                    <MoveFsEntryElementSkeleton />
                    <MoveFsEntryElementSkeleton />
                    <MoveFsEntryElementSkeleton />
                  </>
                )}
              </List>
            </div>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending || !selectedDir} onClick={onAlertDialogOpen} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <CheckCircle2 />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface MoveFsEntryElementProps {
  element: FsEntryElement;
  isCurrentSelected: boolean;
  onOpenSummarize: () => void;
  selectDirectory: (dir: DirectoryPublic) => void;
  onRouteChange: (id: string) => void;
}

function MoveFsEntryElement({ element, onOpenSummarize, selectDirectory, onRouteChange, isCurrentSelected }: MoveFsEntryElementProps) {
  const { dir, isSelected, isWritable } = element;

  const isDisabled = useMemo(() => isSelected || !isWritable, [isWritable, isSelected]);
  const { icon, menuButton } = useMemo(() => {
    let icon: ReactElement<HTMLElement> | undefined = undefined;
    let menuButton: ReactElement<HTMLElement> | undefined = undefined;

    if (isSelected) {
      icon = <FolderKey className={'text-text-muted size-4'} />;
      menuButton = (
        <Badge size={'sm'} border variant={'muted'}>
          Folder Przenoszony
        </Badge>
      );
    } else if (!isWritable) {
      icon = <FolderLock className={'text-text-muted size-4'} />;
      menuButton = (
        <Badge variant={'destructive'} size={'sm'}>
          Tylko do odczytu
        </Badge>
      );
    } else {
      icon = <FolderInput className={'text-text-muted size-4'} />;
      menuButton = (
        <div className="flex shrink-0">
          <Button
            onClick={() => {
              selectDirectory(dir);
              onOpenSummarize();
            }}
            size={'sm'}
            variant={'tertiary'}
          >
            Przenieś
          </Button>
          <Button
            type={'button'}
            onClick={(e) => {
              e.stopPropagation();
              onRouteChange(dir.id);
            }}
            size={'sm'}
            variant={'ghost'}
          >
            <ChevronRight />
          </Button>
        </div>
      );
    }
    return { icon, menuButton };
  }, [dir, isSelected, isWritable, onOpenSummarize, onRouteChange, selectDirectory]);

  return (
    <ListItem
      onClick={() => {
        if (isDisabled) {
          return;
        }
        selectDirectory(dir);
      }}
      className={cn(isDisabled && 'cursor-not-allowed opacity-50', isSelected && 'opacity-75', isCurrentSelected && 'bg-fill-primary-hover')}
    >
      {icon}
      <ListItemTitle className={'mr-auto grow'}>{dir.name}</ListItemTitle>
      {menuButton}
    </ListItem>
  );
}

function MoveFsEntryElementSkeleton() {
  return (
    <li className={cn(listElementVariants())}>
      <Skeleton className={'size-4 rounded-sm'} />
      <Skeleton className={'h-3 w-20 rounded-sm'} />
    </li>
  );
}
